import {useCallback, useMemo, useSyncExternalStore} from "react";
import {useSearchParams} from "react-router-dom";
import {useSetting} from "contexts/setting";
import {useImages} from "contexts/images";
import {useEdits} from "contexts/edits";
import {
  DIMENSION_BY_ID,
  getTemplateMode,
  isScopesChannel,
  sharedSliderValue,
  templatesInMode,
  withTemplateModes,
} from "libs/sceneScope";
import {bgOpsSnapshot, getBgOps, registeredBgTemplateIds, subscribeBgOps} from "libs/bgOps";
import {coverFitTransform} from "libs/utils";
import {getTemplateInstanceSize} from "data/templateManifest";
import {getSizeFromBase64} from "libs/bg-utils";
import {useBannerCrop} from "hooks/useBannerCrop";

/**
 * The one place edits from the sidebar go through on a scopes channel.
 *
 * `scope` is who receives an edit: every banner (the shared value, and every
 * `preset` on that dimension yields to it), or the selected banners (each gets
 * a value of its own and turns `own` on that dimension). Reset and "make
 * global" are the two ways back. See libs/sceneScope.ts for the model.
 *
 * Off a scopes channel every method falls through to the shared write, so a
 * caller can use the hook unconditionally.
 */
/** The edits-context editor for a text record field (`productNames` is the one odd name). */
function textEditor(edits, field) {
  if (field === "productNames") return edits.editProductName;
  return edits[`edit${field[0].toUpperCase()}${field.slice(1)}`];
}

export function useSceneScope({templateIds} = {}) {
  const setting = useSetting();
  const images = useImages();
  const edits = useEdits();
  const [, setSearchParams] = useSearchParams();

  const {
    dataChannel,
    product,
    templateModes,
    editTemplateModes,
    layouts,
    editLayouts,
    variants,
    editVariants,
    base,
    editBase,
    bases,
    editBases,
    scopeMode,
    editScopeMode,
    selectedTemplates,
    editSelectedTemplates,
    defaultTemplateValues,
  } = setting;
  const {
    bgOpacity,
    editBgOpacity,
    bgBrightness,
    editBgBrightness,
    bgOpacities,
    editBgOpacities,
    bgBrightnesses,
    editBgBrightnesses,
    editCustomBgs,
    editCustomBgUploaded,
    editUploadImages,
  } = images;
  // Where a banner's crop goes: see hooks/useBannerCrop.
  const {derived, setCrop, dropCrop} = useBannerCrop();

  const scoped = isScopesChannel(dataChannel);
  const isSelection = scoped && scopeMode === "selection" && selectedTemplates.length > 0;
  const selection = useMemo(() => (isSelection ? selectedTemplates : null), [isSelection, selectedTemplates]);

  // --- mode table --------------------------------------------------------

  /** `dimensions` is one id or a list; every cell lands in one table write. */
  const setModes = useCallback(
    (ids, dimensions, mode) => {
      const next = withTemplateModes(templateModes, ids, [].concat(dimensions), mode);
      if (next !== templateModes) editTemplateModes(next);
    },
    [templateModes, editTemplateModes]
  );

  /**
   * Drop the banners' own values on one dimension; the crop stays (it is
   * positioning, not identity). The layout and variant records are replaced
   * whole, so every id goes into one record: a write per id would start from
   * the record this render closed over and keep only the last id's change.
   */
  const clearValues = useCallback(
    (ids, dimension) => {
      switch (dimension) {
        case "text":
          for (const id of ids) edits.cleanupLocalChanges(id);
          break;
        case "image":
          for (const id of ids) {
            editCustomBgs(id, undefined, true);
            editCustomBgUploaded(id, false);
            editUploadImages(id, undefined, true);
          }
          break;
        case "look": {
          for (const id of ids) {
            editBgOpacities(id, undefined);
            editBgBrightnesses(id, undefined);
          }
          // One record, one write: a write per id would start from the record
          // this render closed over and keep only the last id's change.
          const nextBases = {...bases};
          for (const id of ids) delete nextBases[id];
          editBases(nextBases);
          break;
        }
        case "layout": {
          const next = {...layouts};
          for (const id of ids) delete next[id];
          editLayouts(next);
          break;
        }
        case "variant": {
          const next = {...variants};
          for (const id of ids) delete next[id];
          editVariants(next);
          break;
        }
        default:
          throw new Error(`Unknown dimension "${dimension}"`);
      }
    },
    [edits, editCustomBgs, editCustomBgUploaded, editUploadImages, editBgOpacities, editBgBrightnesses, bases, editBases, layouts, editLayouts, variants, editVariants]
  );

  /** A shared change on `dimensions` (one id or a list): every preset there yields to it. */
  const yieldPresets = useCallback(
    (dimensions, onlyIds) => {
      if (!scoped) return;
      let next = templateModes;
      for (const dimension of [].concat(dimensions)) {
        const ids = templatesInMode(templateModes, dimension, "preset").filter(
          (id) => !onlyIds || onlyIds.includes(id)
        );
        if (!ids.length) continue;
        clearValues(ids, dimension);
        next = withTemplateModes(next, ids, [dimension], undefined);
      }
      if (next !== templateModes) editTemplateModes(next);
    },
    [scoped, templateModes, clearValues, editTemplateModes]
  );

  // --- what the scope's controls SHOW ------------------------------------

  /**
   * The value a sidebar slider must display.
   *
   * The sliders are controlled inputs, and under a selection an edit goes to
   * the banners' own records and deliberately leaves the shared value alone.
   * A slider still bound to the shared number therefore re-renders back to it
   * on every change: the write lands, but the thumb springs back and the
   * control reads as broken. So the scope answers with the value it would
   * edit — the selection's, resolved `own -> shared` the way the banners
   * themselves resolve it (layout/BgTransformer/templateBg.ts).
   *
   * A selection whose banners disagree shows the first one's value. The drag
   * then makes them agree, which is what editing a selection means. With no
   * selection and the page's `templateIds`, the slider shows what the banners
   * it moves show (sharedSliderValue).
   */
  const scopedOpacity = useMemo(
    () =>
      selection
        ? (bgOpacities?.[selection[0]] ?? bgOpacity)
        : sharedSliderValue(templateIds ?? [], bgOpacities, bgOpacity, templateModes),
    [selection, bgOpacities, bgOpacity, templateIds, templateModes]
  );
  const scopedBrightness = useMemo(
    () =>
      selection
        ? (bgBrightnesses?.[selection[0]] ?? bgBrightness)
        : sharedSliderValue(templateIds ?? [], bgBrightnesses, bgBrightness, templateModes),
    [selection, bgBrightnesses, bgBrightness, templateIds, templateModes]
  );
  const scopedBase = useMemo(
    () => (selection ? (bases?.[selection[0]] ?? base) : base),
    [selection, bases, base]
  );

  // --- applying to the scope -------------------------------------------

  const applyOpacity = useCallback(
    (value) => {
      if (selection) {
        for (const id of selection) editBgOpacities(id, value);
        setModes(selection, "look", "own");
        return;
      }
      editBgOpacity(value);
      yieldPresets("look");
    },
    [selection, editBgOpacities, editBgOpacity, setModes, yieldPresets]
  );

  const applyBrightness = useCallback(
    (value) => {
      if (selection) {
        for (const id of selection) editBgBrightnesses(id, value);
        setModes(selection, "look", "own");
        return;
      }
      editBgBrightness(value);
      yieldPresets("look");
    },
    [selection, editBgBrightnesses, editBgBrightness, setModes, yieldPresets]
  );

  /**
   * The fill the artwork sits on. Shared it is the scene's `base`; under a
   * selection each banner gets its own, in the same record shape as the
   * layout and variant scopes.
   */
  const applyBase = useCallback(
    (value) => {
      if (selection) {
        const next = {...bases};
        for (const id of selection) next[id] = value;
        editBases(next);
        setModes(selection, "look", "own");
        return;
      }
      editBase(value);
      yieldPresets("look");
    },
    [selection, bases, editBases, editBase, setModes, yieldPresets]
  );

  /** Layout and variant are URL-addressed when shared; per banner they are records. */
  const applyLayout = useCallback(
    (value) => {
      if (selection) {
        const next = {...layouts};
        for (const id of selection) next[id] = Number(value);
        editLayouts(next);
        setModes(selection, "layout", "own");
        return;
      }
      setSearchParams(
        (params) => {
          params.set("layout", String(value));
          return params;
        },
        {replace: true}
      );
      yieldPresets("layout");
    },
    [selection, layouts, editLayouts, setModes, setSearchParams, yieldPresets]
  );

  const applyVariant = useCallback(
    (value) => {
      if (selection) {
        const next = {...variants};
        for (const id of selection) next[id] = Number(value);
        editVariants(next);
        setModes(selection, "variant", "own");
        return;
      }
      setSearchParams(
        (params) => {
          params.set("variant", String(value));
          return params;
        },
        {replace: true}
      );
      yieldPresets("variant");
    },
    [selection, variants, editVariants, setModes, setSearchParams, yieldPresets]
  );

  /**
   * One cell of the layout grid: a layout and a variant together. One URL
   * write and one mode-table write, since two `setSearchParams` updaters or
   * two `setModes` calls in one handler each start from the state this render
   * closed over, and the second would drop the first.
   */
  const applyLayoutVariant = useCallback(
    (layout, variant) => {
      if (selection) {
        const nextLayouts = {...layouts};
        const nextVariants = {...variants};
        for (const id of selection) {
          nextLayouts[id] = Number(layout);
          nextVariants[id] = Number(variant);
        }
        editLayouts(nextLayouts);
        editVariants(nextVariants);
        setModes(selection, ["layout", "variant"], "own");
        return;
      }
      setSearchParams(
        (params) => {
          params.set("layout", String(layout));
          params.set("variant", String(variant));
          return params;
        },
        {replace: true}
      );
      yieldPresets(["layout", "variant"]);
    },
    [selection, layouts, editLayouts, variants, editVariants, setModes, setSearchParams, yieldPresets]
  );

  /**
   * A gallery pick or an upload for the selected banners only: the image or
   * video becomes each banner's own, cover-fit to the banner. `uploaded` marks
   * a file the user uploaded, which the banner header can then remove. `ids`
   * are the banners selected when the file was picked; an upload lands later.
   * Shared picks stay with the background selector, which calls
   * `yieldPresets("image")` itself.
   */
  const applyBackgroundToSelection = useCallback(
    (imageUrl, {uploaded = false, ids = selection} = {}) => {
      if (!ids?.length || !imageUrl) return false;
      for (const id of ids) {
        editCustomBgs(id, imageUrl, true);
        editCustomBgUploaded(id, uploaded);
      }
      setModes(ids, "image", "own");
      // Cover-fit each banner once the size is known; a video gives it from
      // its metadata.
      getSizeFromBase64(imageUrl).then(
        ({width, height}) => {
          for (const id of ids) {
            const size = getTemplateInstanceSize(dataChannel, id, product.value);
            const {x, y, scale} = coverFitTransform(width, height, size.width, size.height);
            setCrop(id, {x, y, scale, rotate: 0, flip: {x: false, y: false}});
          }
        },
        (error) => console.error("[useSceneScope] cannot measure the background", error)
      );
      return true;
    },
    [selection, editCustomBgs, editCustomBgUploaded, dataChannel, product, setCrop, setModes]
  );

  /**
   * fit | flipH | flipV | rotate | reset, on every banner in scope.
   *
   * `reset` puts the artwork back where the default scene (or the catalog)
   * places it, by dropping each banner's manual crop — see `resetToDefault`
   * in layout/BgTransformer. It is the one operation that leaves the look
   * modes alone: the banner shows the derived crop again, which is exactly
   * what "preset" and "shared" mean, so nothing is set apart.
   */
  const applyBgOperation = useCallback(
    (op, arg = 90) => {
      const own = new Set(templatesInMode(templateModes, "look", "own"));
      const ids = selection ?? registeredBgTemplateIds().filter((id) => op === "reset" || !own.has(id));
      const changed = [];
      for (const id of ids) {
        // A ready cut (BgTransformer/templateBg.ts) has no positioning and
        // registers no operations; it is left alone, its look modes too.
        const ops = getBgOps(id);
        if (!ops) continue;
        if (op === "fit") ops.coverFit();
        else if (op === "flipH") ops.flipH();
        else if (op === "flipV") ops.flipV();
        else if (op === "rotate") ops.rotate(arg ?? 90);
        else if (op === "reset") ops.reset();
        else throw new Error(`Unknown background operation "${op}"`);
        changed.push(id);
      }
      if (op === "reset" || changed.length === 0) return;
      if (selection) setModes(changed, "look", "own");
      else yieldPresets("look", changed);
    },
    [selection, templateModes, setModes, yieldPresets]
  );

  // The banners in scope that have positioning. A ready cut registers no
  // operations, so a scope with only ready cuts has no targets, and the
  // sidebar shows no positioning for it.
  const positionedTemplates = useSyncExternalStore(subscribeBgOps, bgOpsSnapshot);
  const bgOperationTargets = useMemo(() => {
    const positioned = new Set(positionedTemplates);
    return (selection ?? positionedTemplates).filter((id) => positioned.has(id));
  }, [selection, positionedTemplates]);

  // --- ways back ---------------------------------------------------------

  const restoreDefault = useCallback(
    (id, dimension) => {
      const values = defaultTemplateValues?.[id]?.[dimension];
      if (!values) return false;
      for (const [field, value] of Object.entries(values)) {
        if (DIMENSION_BY_ID[dimension].edits.includes(field)) {
          textEditor(edits, field)?.(value, id, true);
        } else if (field === "customBgs") editCustomBgs(id, value, true);
        else if (field === "uploadImages") editUploadImages(id, value, true);
        else if (field === "bgOpacities") editBgOpacities(id, value);
        else if (field === "bgBrightnesses") editBgBrightnesses(id, value);
        else if (field === "bases") editBases({...bases, [id]: value});
        else if (field === "layouts") editLayouts({...layouts, [id]: value});
        else if (field === "variants") editVariants({...variants, [id]: value});
      }
      return true;
    },
    [defaultTemplateValues, edits, editCustomBgs, editUploadImages, editBgOpacities, editBgBrightnesses, bases, editBases, layouts, editLayouts, variants, editVariants]
  );

  /** Back to the default's preset when there is one, else to the shared value. */
  const resetTemplate = useCallback(
    (id, dimension, to = "default") => {
      clearValues([id], dimension);
      // The crop that fitted the banner's own image does not fit the image it
      // goes back to, so the derived crop of that image shows again. A look of
      // the banner's own keeps its crop. Off derivation the records are the
      // only crop the banner has, so they stay.
      if (dimension === "image" && derived && getTemplateMode(templateModes, id, "look") !== "own") {
        dropCrop(id);
      }
      if (to === "default" && restoreDefault(id, dimension)) {
        setModes([id], dimension, "preset");
      } else {
        setModes([id], dimension, undefined);
      }
    },
    [clearValues, derived, templateModes, dropCrop, restoreDefault, setModes]
  );

  /** Promote a banner's own value to the shared one, then re-attach it. */
  const makeGlobal = useCallback(
    (id, dimension) => {
      switch (dimension) {
        case "text":
          for (const field of DIMENSION_BY_ID.text.edits) {
            const value = edits[field]?.[id];
            if (value !== undefined) textEditor(edits, field)?.(value, "main", false);
          }
          break;
        case "look":
          if (bgOpacities?.[id] !== undefined) editBgOpacity(bgOpacities[id]);
          if (bgBrightnesses?.[id] !== undefined) editBgBrightness(bgBrightnesses[id]);
          if (bases?.[id] !== undefined) editBase(bases[id]);
          break;
        case "layout":
          if (layouts?.[id] !== undefined) applyLayout(layouts[id]);
          break;
        case "variant":
          if (variants?.[id] !== undefined) applyVariant(variants[id]);
          break;
        case "image":
          // A banner's own image has no shared slot to go to (the shared
          // background is a gallery pick); nothing to promote.
          break;
        default:
          throw new Error(`Unknown dimension "${dimension}"`);
      }
      resetTemplate(id, dimension, "global");
      yieldPresets(dimension);
    },
    [edits, bgOpacities, bgBrightnesses, bases, editBgOpacity, editBgBrightness, editBase, layouts, variants, applyLayout, applyVariant, resetTemplate, yieldPresets]
  );

  /** Set a banner apart on a dimension, keeping what it shows now. */
  const detach = useCallback(
    (id, dimension) => {
      if (dimension === "look") {
        editBgOpacities(id, bgOpacities?.[id] ?? bgOpacity ?? 100);
        editBgBrightnesses(id, bgBrightnesses?.[id] ?? bgBrightness ?? 100);
        editBases({...bases, [id]: bases?.[id] ?? base});
      }
      setModes([id], dimension, "own");
    },
    [editBgOpacities, editBgBrightnesses, editBases, bgOpacities, bgBrightnesses, bases, bgOpacity, bgBrightness, base, setModes]
  );

  // --- selection ---------------------------------------------------------

  const toggleSelected = useCallback(
    (id) => {
      const next = selectedTemplates.includes(id)
        ? selectedTemplates.filter((x) => x !== id)
        : [...selectedTemplates, id];
      editSelectedTemplates(next);
      // Ticking the first banner is what the user means by "edit these".
      if (next.length && scopeMode !== "selection") editScopeMode("selection");
      if (!next.length) editScopeMode("all");
    },
    [selectedTemplates, editSelectedTemplates, scopeMode, editScopeMode]
  );

  const clearSelection = useCallback(() => {
    editSelectedTemplates([]);
    editScopeMode("all");
  }, [editSelectedTemplates, editScopeMode]);

  return {
    scoped,
    isSelection,
    selection,
    scopeMode,
    selectedTemplates,
    editScopeMode,
    toggleSelected,
    clearSelection,
    modeOf: (id, dimension) => getTemplateMode(templateModes, id, dimension),
    scopedOpacity,
    scopedBrightness,
    scopedBase,
    applyOpacity,
    applyBrightness,
    applyBase,
    applyLayout,
    applyVariant,
    applyLayoutVariant,
    applyBackgroundToSelection,
    applyBgOperation,
    bgOperationTargets,
    yieldPresets,
    resetTemplate,
    makeGlobal,
    detach,
  };
}
